import { NumericDictionary } from 'lodash';
import { GenPartial } from 'typeormgen';
import BudgetCharacter from '../budget-characters/budget-character.entity';
import ChangeEpisodicItem from '../change-episodic-items/change-episodic-item.entity';
import ChangeScene from '../change-scenes/change-scene.entity';
import Change from '../changes/change.entity';
import CharPicture from '../char-pictures/char-picture.entity';
import CharScene from '../char-scenes/char-scene.entity';
import Character from '../characters/character.entity';
import CmChangePicture from '../cm-change-pictures/cm-change-picture.entity';
import CmEpisodicCharacter from '../cm-episodic-characters/cm-episodic-character.entity';
import { DEPARTMENTS } from '../enums/enums';
import EpisodicActor from '../episodic-actors/episodic-actor.entity';
import EpisodicCharacterPicture from '../episodic-character-pictures/episodic-character-picture.entity';
import EpisodicItem from '../episodic-items/episodic-item.entity';
import EpisodicProp from '../episodic-props/episodic-prop.entity';
import HaChangeScene from '../ha-change-scenes/ha-change-scene.entity';
import HaChange from '../ha-changes/ha-change.entity';
import HaEpisodicCharacter from '../ha-episodic-characters/ha-episodic-character.entity';
import HaLookPicture from '../ha-look-pictures/ha-look-picture.entity';
import MuChangeScene from '../mu-change-scenes/mu-change-scene.entity';
import MuChange from '../mu-changes/mu-change.entity';
import MuEpisodicCharacter from '../mu-episodic-characters/mu-episodic-character.entity';
import MuLookPicture from '../mu-look-pictures/mu-look-picture.entity';
import PrEpisodicCharacter from '../pr-episodic-characters/pr-episodic-character.entity';
import Production from '../productions/production.entity';
import PropSceneCharScene from '../prop-scene-char-scenes/prop-scene-char-scene.entity';
import PropScene from '../prop-scenes/prop-scene.entity';
import Scene from '../scenes/scene.entity';
import {
	SosCreateResponse,
	SosDeleteResponse,
	SosGetResponse,
	SosListResponse,
	SosUpdateResponse,
} from '../sos-response';
import { EpisodicBaseESDoc } from '../types';
import EpisodicCharacter from './episodic-character.entity';

export interface BgCharSearchResponse extends SosListResponse {
	listed: {
		Character: Character[];
		EpisodicCharacter: EpisodicCharacter[];
	};
}

export interface EpisodicCharacterESDoc extends EpisodicBaseESDoc {
	first_name: string;
	last_name: string;
	char_num: string;
	is_primary: number;
}

export interface EpisodicCharacterCreateResponse extends SosCreateResponse {
	created: {
		EpisodicCharacter: EpisodicCharacter[];
		Character: Character[];
		EpisodicActor: EpisodicActor[];
	};
}

export interface EpisodicCharacterExportToNewSeasonReponse
	extends SosUpdateResponse {
	updated: {
		EpisodicCharacter: EpisodicCharacter[];
	};
}

export interface EpisodicCharacterExportToNewSeasonCheckReponse {
	epCharIds: number[];
	exportInProgress: boolean;
}

export interface EpisodicCharacterGetResponse extends SosGetResponse {
	listed: {
		EpisodicCharacter: EpisodicCharacter[];
		Character: Character[];
		EpisodicActor: EpisodicActor[];
	};
	next: number;
	previous: number;
}

export interface EpisodicCharacterListResponse extends SosListResponse {
	listed: {
		EpisodicCharacter: EpisodicCharacter[];
		Character: Character[];
		EpisodicActor: EpisodicActor[];
	};
}

export interface UpdateMultipleEpCharPayload {
	episodicCharacters: GenPartial<EpisodicCharacter>[];
	characters?: GenPartial<Character>[];
	addedProdIds?: NumericDictionary<number[]>;
	removedProdIds?: NumericDictionary<number[]>;
	addedSceneIds?: NumericDictionary<number[]>;
	removedSceneIds?: NumericDictionary<number[]>;
	epActorsToCreateByEpCharId?: NumericDictionary<Partial<EpisodicActor>>;
}

export interface EpisodicCharacterUpdateResponse extends SosUpdateResponse {
	created: {
		Character?: Character[];
		CharScene?: CharScene[];
		EpisodicActor?: EpisodicActor[];
	};
	destroyed: {
		Change?: Change[];
		ChangeEpisodicItem?: ChangeEpisodicItem[];
		ChangeScene?: ChangeScene[];
		Character?: Character[];
		CharScene?: CharScene[];
		CmChangePicture?: CmChangePicture[];
		HaChange?: HaChange[];
		HaChangeScene?: HaChangeScene[];
		HaLookPicture?: HaLookPicture[];
		MuChange?: MuChange[];
		MuChangeScene?: MuChangeScene[];
		MuLookPicture?: MuLookPicture[];
		PropScene?: PropScene[];
		PropSceneCharScene?: PropSceneCharScene[];
	};
	updated: {
		Change?: Change[];
		EpisodicCharacter: EpisodicCharacter[];
		Character?: Character[];
		HaChange?: HaChange[];
		MuChange?: MuChange[];
	};
}

export interface EpisodicCharacterDestroyResponse extends SosDeleteResponse {
	destroyed: {
		Change?: Change[];
		ChangeEpisodicItem?: ChangeEpisodicItem[];
		ChangeScene?: ChangeScene[];
		Character?: Character[];
		CharPicture?: CharPicture[];
		CharScene?: CharScene[];
		CmChangePicture?: CmChangePicture[];
		EpisodicCharacter?: EpisodicCharacter[];
		EpisodicCharacterPicture?: EpisodicCharacterPicture[];
		HaChange?: HaChange[];
		HaChangeScene?: HaChangeScene[];
		HaLookPicture?: HaLookPicture[];
		MuChange?: MuChange[];
		MuChangeScene?: MuChangeScene[];
		MuLookPicture?: MuLookPicture[];
		PropScene?: PropScene[];
		PropSceneCharScene?: PropSceneCharScene[];
	};
	updated: {
		EpisodicItem?: EpisodicItem[];
		EpisodicProp?: EpisodicProp[];
	};
}

export interface EpisodicCharacterUpdateCharNumsResponse
	extends SosUpdateResponse {
	updated: {
		Character: Character[];
		EpisodicCharacter: EpisodicCharacter[];
	};
}

export interface EpisodicCharacterGetDeleteCountsResponse {
	cmChanges: number;
	episodicItems: number;
	episodicProps: number;
	muChanges: number;
	haChanges: number;
	productions: number;
}

export interface EpisodicCharacterMergeConflictGetResponse {
	conflicted: {
		resolvable?: {
			[x: number]: Object | boolean | number | string;
		};
		unresolvable?:
			| {
					[id: number]: boolean | Scene[] | Production[];
			  }
			| {
					scenes: Scene[];
					changesBySceneId: { [sceneId: number]: boolean };
					haChangesBySceneId: { [sceneId: number]: boolean };
					muChangesBySceneId: { [sceneId: number]: boolean };
			  };
	};
	otherDepts?: DEPARTMENTS[];
}

export interface EpisodicCharacterMergePayload {
	EpisodicCharacter: Partial<EpisodicCharacter>;
	BudgetCharacter?: Partial<BudgetCharacter>[];
}

export interface EpisodicCharacterMergeResponse {
	created: {
		ChangeScene?: ChangeScene[];
		Character?: Character[];
		CharScene?: CharScene[];
		HaChangeScene?: HaChangeScene[];
		MuChangeScene?: MuChangeScene[];
		PropSceneCharScene?: PropSceneCharScene[];
	};
	updated: {
		BudgetCharacter?: BudgetCharacter[];
		Character?: Character[];
		Change?: Change[];
		CmEpisodicCharacter?: CmEpisodicCharacter[];
		EpisodicCharacter: EpisodicCharacter[];
		EpisodicCharacterPicture?: EpisodicCharacterPicture[];
		HaChange?: HaChange[];
		HaEpisodicCharacter?: HaEpisodicCharacter[];
		MuChange?: MuChange[];
		MuEpisodicCharacter?: MuEpisodicCharacter[];
		PrEpisodicCharacter?: PrEpisodicCharacter[];
	};
	destroyed: {
		ChangeScene?: ChangeScene[];
		CharScene?: CharScene[];
		CmEpisodicCharacter?: CmEpisodicCharacter[];
		EpisodicCharacter?: EpisodicCharacter[];
		EpisodicCharacterPicture?: EpisodicCharacterPicture[];
		HaChangeScene?: HaChangeScene[];
		HaEpisodicCharacter?: HaEpisodicCharacter[];
		MuChangeScene?: MuChangeScene[];
		MuEpisodicCharacter?: MuEpisodicCharacter[];
		PrEpisodicCharacter?: PrEpisodicCharacter[];
		PropSceneCharScene?: PropSceneCharScene[];
	};
}
